/**
 * Componente para exibir quando uma análise foi ignorada (SKIPPED) durante o upload
 * e pode ser executada sob demanda pelo usuário.
 * 
 * Exibe uma mensagem informativa clara e um botão para executar a análise.
 * Preparado para suportar restrições de plano no futuro.
 */

import React, { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Typography
} from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import BlockIcon from '@mui/icons-material/Block'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { runOnDemandAnalysis } from '../../../services/api'

type Props = {
  /** Nome legível da análise (ex: "Análise de Sentimento") */
  title: string
  /** Tipo da análise para o backend (ex: "sentiment") */
  analysisType: string
  /** ID do arquivo */
  fileId: string
  /** Callback chamado quando a análise foi iniciada/concluída com sucesso */
  onAnalysisStarted?: () => void
  /** Se o plano do usuário restringe essa análise (futuro) */
  planRestricted?: boolean
}

export default function AnalysisSkippedCard({ 
  title, 
  analysisType, 
  fileId, 
  onAnalysisStarted,
  planRestricted = false 
}: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleRun = async () => {
    setIsSubmitting(true)
    setError(null)
    try {
      await runOnDemandAnalysis(fileId, analysisType)
      // Notifica o pai para atualizar o status / recarregar dados
      onAnalysisStarted?.()
    } catch (e: any) {
      console.error(`Erro ao executar ${analysisType}:`, e)
      setError(
        e?.message || 'Ocorreu um erro ao iniciar a análise. Tente novamente em alguns instantes.'
      )
      setIsSubmitting(false)
    }
  }

  return (
    <Card elevation={0} sx={{ border: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
      <CardContent sx={{ textAlign: 'center', py: 5 }}>
        {/* Ícone */}
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            bgcolor: planRestricted ? '#fce4ec' : '#e3f2fd',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2
          }}
        >
          {planRestricted ? (
            <BlockIcon sx={{ fontSize: 36, color: '#e57373' }} />
          ) : (
            <InfoOutlinedIcon sx={{ fontSize: 36, color: '#1976d2' }} />
          )}
        </Box>

        {/* Título */}
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 1 }}>
          {title}
        </Typography>

        {/* Mensagem informativa */}
        {planRestricted ? (
          <Typography variant="body2" sx={{ color: '#666', mb: 3, maxWidth: 450, mx: 'auto' }}>
            Esta análise não está disponível no seu plano atual. 
            Entre em contato para conhecer os planos que incluem esta funcionalidade.
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ color: '#666', mb: 3, maxWidth: 450, mx: 'auto' }}>
            Esta análise não foi solicitada no momento do envio do arquivo.
            Você pode executá-la agora a qualquer momento — os resultados ficarão disponíveis em instantes.
          </Typography>
        )}

        {/* Erro */}
        {error && (
          <Alert severity="error" sx={{ mb: 2, maxWidth: 450, mx: 'auto', textAlign: 'left' }}>
            {error}
          </Alert>
        )}

        {/* Botão de ação */}
        {!planRestricted && (
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={handleRun}
            disabled={isSubmitting}
            sx={{ 
              textTransform: 'none',
              px: 4,
              py: 1,
              borderRadius: 2,
              fontWeight: 600
            }}
          >
            {isSubmitting ? 'Iniciando...' : 'Executar Análise'}
          </Button>
        )}
      </CardContent>
    </Card>
  )
}
